import { useEffect, useRef, type ReactNode } from 'react';
import { CornersIn, CornersOut, List, X } from '@phosphor-icons/react';
import Tooltip from '@/components/ui/tooltip';
import ExamSelectors from '../components/ora/Global/ExamSelectors/ExamSelectors';
import NotificationsBell from '../components/ora/Home/HomeHeader/NotificationsBell';
import { useMatchMedia } from '../hooks/useMatchMedia';
import type { OsWindowMenuAction } from './OsWindowMenuAction';
import { OS_DESKTOP_MEDIA_QUERY, type OsWindowVariant } from './osRouteMeta';

interface OsWindowProps {
  menuAction: OsWindowMenuAction | null;
  children: ReactNode;
  /**
   * The window persists across routes; when this key changes (window group,
   * i.e. a different page rather than an in-group drill), the scroll
   * container resets to the top — the behavior remounting used to provide.
   */
  scrollResetKey: string;
  isExiting: boolean;
  /**
   * Paint in place instead of animating in (the window a session opens on).
   * Read once, at mount: the class must not flip on a later in-window route,
   * or the enter animation would replay.
   */
  isInstant?: boolean;
  /** User-driven edge-to-edge state; only meaningful for default windows. */
  isMaximized: boolean;
  onClose: () => void;
  onToggleMaximize: () => void;
  /** Page name; names the window for assistive tech (the header shows none). */
  title?: string;
  /** A page-owned control beside the window controls (`useOsWindowHeaderAddon`). */
  headerAddon?: ReactNode;
  /** Drops the header bar below the OS breakpoint (the Dashboard). */
  hidesMobileHeader?: boolean;
  /** Centers the exam selector in the header (the Dashboard only). */
  showsExamSelectors?: boolean;
  variant: OsWindowVariant;
}

const OsWindow = ({
  menuAction,
  children,
  scrollResetKey,
  isExiting,
  isInstant = false,
  isMaximized,
  onClose,
  onToggleMaximize,
  title,
  headerAddon,
  hidesMobileHeader = false,
  showsExamSelectors = false,
  variant,
}: OsWindowProps) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const isInstantRef = useRef(isInstant);
  // Sub-md the bell lives in the Dashboard page header; mounting a hidden
  // one here would fetch the unread count a second time.
  const isDesktopViewport = useMatchMedia(OS_DESKTOP_MEDIA_QUERY);

  useEffect(() => {
    bodyRef.current?.scrollTo(0, 0);
  }, [scrollResetKey]);

  return (
    <section
      aria-label={title ? `${title} window` : 'Ora window'}
      className={`os-window os-window--${variant} ${
        variant === 'default' && isMaximized ? 'os-window--maximized' : ''
      } ${isExiting ? 'os-window--exiting' : ''} ${
        isInstantRef.current ? 'os-window--instant' : ''
      }`}
    >
      {variant !== 'fullscreen' && (
        <header
          className={`os-window__header ${
            hidesMobileHeader ? 'os-window__header--desktop-only' : ''
          }`}
        >
          <div className="os-window__header-side">
            {menuAction && (
              <Tooltip placement="bottom" title={`Open ${menuAction.label}`}>
                <button
                  aria-label={`Open ${menuAction.label}`}
                  className="os-window__menu"
                  onClick={menuAction.onOpen}
                  type="button"
                >
                  <List aria-hidden="true" size={14} />
                </button>
              </Tooltip>
            )}
            {title && <span className="os-window__title">{title}</span>}
          </div>
          {/* Hidden below 900 via CSS; the page header owns it there. */}
          {showsExamSelectors ? (
            <div className="os-window__header-center">
              <ExamSelectors variant="header" />
            </div>
          ) : null}
          <div className="os-window__header-side">
            {headerAddon}
            {/* The bare desktop's bell follows the user into the window. */}
            {isDesktopViewport && (
              <NotificationsBell variant="window-control" />
            )}
            <Tooltip
              placement="bottom"
              title={isMaximized ? 'Restore (m)' : 'Maximize (m)'}
            >
              <button
                aria-label={isMaximized ? 'Restore window' : 'Maximize window'}
                className="os-window__maximize"
                onClick={onToggleMaximize}
                type="button"
              >
                {isMaximized ? (
                  <CornersIn aria-hidden="true" size={14} />
                ) : (
                  <CornersOut aria-hidden="true" size={14} />
                )}
              </button>
            </Tooltip>
            <Tooltip placement="bottom" title="Close (esc)">
              <button
                aria-label="Close window"
                className="os-window__close"
                onClick={onClose}
                type="button"
              >
                <X aria-hidden="true" size={14} />
              </button>
            </Tooltip>
          </div>
        </header>
      )}
      <div className="os-window__body" ref={bodyRef}>
        <div className="os-window__content">{children}</div>
      </div>
    </section>
  );
};

export default OsWindow;
